// src/features/activity/MappingFormDialog.tsx
// Create/edit a Kafka topic → Activity mapping config. Admin only.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { activityActivityMappingStore } from '@core/stores';
import { Button, Input, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import type { ActivityCategory, ActivityMappingConfig, ActivityScope } from '@core/types/activity';
import { ActivityModal } from './Modal';
import { CATEGORY_VALUES, SCOPE_VALUES, categoryKey, scopeKey, makeEntity, errMessage } from './logic';

interface MappingFormDialogProps {
  open: boolean;
  mapping: ActivityMappingConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

const empty = (): ActivityMappingConfig => ({
  kafka_topic: '',
  event_type: '',
  category: 'ACTIVITY_CATEGORY_ACTION',
  verb: '',
  summary_template: '',
  scope_field: '',
  default_scope: 'ACTIVITY_SCOPE_ENTITY',
  requires_ack: false,
  ack_target_field: '',
  enabled: true,
});

export const MappingFormDialog = observer(({ open, mapping, onClose, onSaved }: MappingFormDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const [form, setForm] = useState<ActivityMappingConfig>(empty());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(mapping ?? empty());
    setError(null);
  }, [open, mapping]);

  const patch = (partial: Partial<ActivityMappingConfig>) => setForm((prev) => ({ ...prev, ...partial }));

  const isEdit = !!mapping?.entity?.id;

  const submit = async () => {
    if (!form.kafka_topic.trim() || !form.event_type.trim() || !form.verb.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && mapping?.entity?.id) {
        await activityActivityMappingStore.UpdateMappingConfig(mapping.entity.id, { id: mapping.entity.id, config: form });
      } else {
        await activityActivityMappingStore.CreateMappingConfig({
          config: { ...form, entity: form.entity ?? makeEntity('', 'activity_mapping_config', scope) },
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('activity.settings.mappings.form.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ActivityModal
      open={open}
      title={isEdit ? t('activity.settings.mappings.form.editTitle') : t('activity.settings.mappings.form.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={saving} disabled={!form.kafka_topic.trim() || !form.event_type.trim() || !form.verb.trim()}>
            {t('activity.settings.mappings.form.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorAlert error={error} />}
        <div className="grid grid-cols-2 gap-2">
          <Input label={t('activity.settings.mappings.form.kafkaTopic')} value={form.kafka_topic} onChange={(e) => patch({ kafka_topic: e.target.value })} required />
          <Input label={t('activity.settings.mappings.form.eventType')} value={form.event_type} onChange={(e) => patch({ event_type: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Select
            label={t('activity.settings.mappings.form.category')}
            value={form.category ?? 'ACTIVITY_CATEGORY_ACTION'}
            onChange={(e) => patch({ category: e.target.value as ActivityCategory })}
            options={CATEGORY_VALUES.map((c) => ({ value: c, label: t(`activity.category.${categoryKey(c)}`) }))}
          />
          <Input label={t('activity.settings.mappings.form.verb')} value={form.verb} onChange={(e) => patch({ verb: e.target.value })} required />
        </div>
        <Input
          label={t('activity.settings.mappings.form.summaryTemplate')}
          helperText={t('activity.settings.mappings.form.summaryTemplateHelp')}
          value={form.summary_template}
          onChange={(e) => patch({ summary_template: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input label={t('activity.settings.mappings.form.scopeField')} value={form.scope_field} onChange={(e) => patch({ scope_field: e.target.value })} />
          <Select
            label={t('activity.settings.mappings.form.defaultScope')}
            value={form.default_scope ?? 'ACTIVITY_SCOPE_ENTITY'}
            onChange={(e) => patch({ default_scope: e.target.value as ActivityScope })}
            options={SCOPE_VALUES.map((s) => ({ value: s, label: t(`activity.scope.${scopeKey(s)}`) }))}
          />
        </div>
        <ToggleSwitch label={t('activity.settings.mappings.form.requiresAck')} checked={form.requires_ack} onChange={(v) => patch({ requires_ack: v })} />
        {form.requires_ack && (
          <Input label={t('activity.settings.mappings.form.ackTargetField')} value={form.ack_target_field} onChange={(e) => patch({ ack_target_field: e.target.value })} />
        )}
        <ToggleSwitch label={t('activity.settings.mappings.form.enabled')} checked={form.enabled} onChange={(v) => patch({ enabled: v })} />
      </div>
    </ActivityModal>
  );
});

export default MappingFormDialog;
